import React, { useCallback, useMemo } from 'react'
import { styled } from '~/app/components'
import SharedOrderListCardItem from './SharedOrderListCardItem'
import useSharedOrderConverter, {
  CategoryOrder,
  CategoryOrders,
} from '~/app/components/order-converter/useSharedOrderConverter'
import { SERVICE_TYPE } from '~/app/utils/OrderListUtils'
import { useRouter } from 'next/router'
import { CategoryType } from '~/__generated__/__types__'
import { Icon_info } from '@yanolja-org/ya-com-system-icon'
import { Typography } from '@yanolja-org/ya-com-core'

/**
 * SharedOrderList component
 * author: soojin.oh
 */

type SharedOrderListProps = {
  categoryOrders?: CategoryOrders
}

// styled components
const Container = styled.div``
const Notice = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1.4rem 1fr;
  gap: 0.2rem;
  padding: 0 1rem 1rem 1rem;
  ${Typography('body2.multiline').regular};
  color: ${({ theme }) => theme.color.gray600};
`
const InfoIcon = styled(Icon_info)`
  margin-top: 0.1rem;
  path {
    fill: ${({ theme }) => theme.color.gray600};
  }
`

// SharedOrderList component
const SharedOrderList: React.FC<SharedOrderListProps> = function ({ categoryOrders }) {
  const { toSharedDomesticOrder, toSharedLeisureOrder, toSharedTrainOrder, toSharedBusOrder } =
    useSharedOrderConverter()
  const categoryOrderToCardOrder = useCallback(
    (order: CategoryOrder) =>
      order.__typename === 'SharedOrderDomestic'
        ? toSharedDomesticOrder(order)
        : order.__typename === 'SharedOrderLeisure'
          ? toSharedLeisureOrder(order)
          : order.__typename === 'SharedOrderTrain'
            ? toSharedTrainOrder(order)
            : order.__typename === 'SharedOrderBus'
              ? toSharedBusOrder(order)
              : null,
    []
  )
  const { push } = useRouter()

  /**
   * 기차와 버스는 교통이라는 단어로 service type 을 공유하므로 categoryType 을 사용함.
   * facebook 공유하기는 쿼리스트링이 붙어 useRouter 의 asPath 를 사용하면 상세 페이지로 이동 불가
   */
  const onItemClick = useCallback(
    (serviceType: SERVICE_TYPE, id: string, categoryType?: CategoryType) => {
      const service =
        SERVICE_TYPE.ACCOMMODATION === serviceType
          ? 'accommodation'
          : CategoryType.DomesticBus === categoryType
            ? 'bus'
            : CategoryType.DomesticTrain === categoryType
              ? 'train'
              : 'leisure'
      push(`${location.pathname}/${service}/${id}${location.search}`)
    },
    []
  )

  const cardOrderToCardItem = useCallback(
    (order, index) => (
      <SharedOrderListCardItem
        key={index}
        order={order}
        onCardClick={onItemClick}
        clickable={true}
      />
    ),
    []
  )

  const cards = useMemo(
    () => categoryOrders?.map(categoryOrderToCardOrder).flat(1).map(cardOrderToCardItem),
    [categoryOrders]
  )

  return (
    <Container>
      <Notice>
        <InfoIcon size={1.4} unit="rem" />
        <div>
          이용 가능한 예약 내역만 노출됩니다. 이용을 완료하거나 취소 요청된 이용권은 노출되지
          않습니다.
        </div>
      </Notice>
      {cards}
    </Container>
  )
}

export default SharedOrderList
